import { useResource } from '@landsbankinn/l16n';
import React, { createContext, useContext, useState } from 'react';
import { useApplicationServerContext } from '../../../../../state';
import { client as prismicClient } from './prismic';
import * as prismic from '@prismicio/client';
import { internal } from 'src/services/ConsoleLoggerService';
import { isCorporate } from '../../../../isCorporate';
import { useBrand } from 'src/hooks/useBrand';

type MarketingContentDataAd = {
  imageUrl: string;
  text?: string;
  linkUrl?: string;
};

type MarketingContentData = {
  ad: MarketingContentDataAd;
};

type PrismicSliceProductCards = prismic.Slice<
  'product_cards',
  Record<string, never>,
  {
    copy: prismic.RichTextField;
    heading: prismic.RichTextField;
    image: prismic.ImageField<'ar_1_1'>;
    link: prismic.LinkField;
  }
>;

type PrismicSliceText = prismic.Slice<'text', { text: prismic.RichTextField }>;

type PrismicPage = prismic.PrismicDocument<{
  body: [PrismicSliceProductCards, PrismicSliceText, PrismicSliceText];
}>;

const MarketingContentContext = createContext<MarketingContentData | undefined>(
  undefined,
);
export const PrismicContentContextProvider = (props: {
  children: React.ReactNode;
}) => {
  const [data, setData] = useState<MarketingContentData | undefined>();
  const { r, locale } = useResource();
  const [serverState] = useApplicationServerContext();
  const brand = useBrand();
  React.useEffect(() => {
    // Determine if this is the corporate client
    const isCorporateClient = isCorporate(serverState.clientName['is']);

    const fetchData = async () => {
      // Using the prismic client to fetch https://landsbankinn.is/idp-is page
      // This page is stored in the Prismic CMS and provides images/text/links to display
      // as marketing content.
      let pageName =
        brand === 'TM' ? 'idp-tm' : isCorporateClient ? 'idp-fbl' : 'idp';
      if (brand !== 'TM') {
        pageName = locale ? `${pageName}-${locale}` : `${pageName}-is`;
      }

      const document = await prismicClient.getByUID<PrismicPage>(
        'page',
        pageName,
      );
      const slices = document.data.body;

      // Fetching data this manually is by no means correct and is only for temporary use until
      // we can use @landsbankinn/prismic and @landsbankinn/slices in the IDP UI project
      // so the same UI components are used between landsbankinn.is and the IDP UI application.
      const productCards = slices[0].items[0];

      const data: MarketingContentData = {
        ad: {
          imageUrl: productCards.image.ar_1_1.url || '',
          text: prismic.asText(productCards.copy) || '',
          linkUrl: prismic.asLink(productCards.link) || '',
        },
      };

      setData(data);
    };

    // Fetch data and/or show predefined data on error
    fetchData().catch((err) => {
      internal.warn(err);
      let marketingData: MarketingContentDataAd = {
        imageUrl:
          'https://images.prismic.io/landsbankinn/0d154aff-96d9-430b-985b-92cb8206d356_Landsbankinn_Uloma_V4A0441.png?auto=compress,format',
        text: 'Pantaðu tíma fyrir ráðgjöf og þjónustu',
        linkUrl: 'https://www.landsbankinn.is/panta-tima',
      };
      if (isCorporateClient) {
        marketingData = {
          imageUrl:
            'https://images.prismic.io/landsbankinn/161b3dc7-c253-4e20-beb1-441d197b957c_2021-08-27-Taeknimenn.jpg?auto=compress,format',
          text: 'Við bjóðum fyrirtæki velkomin í Landsbankaappið',
          linkUrl: 'https://www.landsbankinn.is/fyrirtaeki/appid',
        };
      }
      if (brand === 'TM') {
        marketingData = {
          imageUrl:
            'https://landsbankinn.cdn.prismic.io/landsbankinn/ajPLk41P9HI4UrkD_Hero_Hero_heimili.svg',
        };
      }
      // This data is used if fetching content from prismic failes
      setData({
        ad: marketingData,
      });
    });
  }, [locale, serverState, brand, r]);

  return (
    <MarketingContentContext.Provider value={data} children={props.children} />
  );
};

export function usePrismicContent() {
  const data = useContext(MarketingContentContext);
  return data;
}
